import React, { useState, useEffect, useMemo } from "react";
import "../styles/societes.css";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/mailsTypesQuill.css";
import { db } from "../firebase";
import { collection, addDoc, Timestamp, onSnapshot, query, orderBy, doc, deleteDoc, getDoc, updateDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "../components/Footer";
import { logUserAction } from "../utils/logUtils";
import { auth } from "../firebase";

// Interface pour les types de mails
interface MailType {
  value: string;
  label: string;
  id?: string; // ID du document Firestore (pour les types depuis Firestore)
}

// Types par défaut (utilisés si aucun type n'est trouvé dans Firestore)
const DEFAULT_MAIL_TYPES: MailType[] = [
  { value: "fda", label: "Enregistrement FDA" },
  { value: "conseil", label: "Conseil" },
  { value: "autres", label: "Autres" },
];

// Barre d'outils personnalisée avec icônes explicites
const CustomToolbar = () => (
  <div id="custom-toolbar">
    <select className="ql-header" defaultValue="" onChange={e => e.persist()}>
      <option value="1">Titre 1</option>
      <option value="2">Titre 2</option>
      <option value="">Normal</option>
    </select>
    <button className="ql-bold" />
    <button className="ql-italic" />
    <button className="ql-underline" />
    <button className="ql-strike" />
    <button className="ql-color" />
    <button className="ql-background" />
    <select className="ql-font" defaultValue="" onChange={e => e.persist()}>
      <option value="sans-serif">Sans Serif</option>
      <option value="serif">Serif</option>
      <option value="monospace">Monospace</option>
    </select>
    {/* Icônes d'alignement personnalisées */}
    <button className="ql-align" value="" title="Aligner à gauche">
      <span style={{fontWeight:'bold'}}>⯇</span>
    </button>
    <button className="ql-align" value="center" title="Centrer">
      <span style={{fontWeight:'bold'}}>≡</span>
    </button>
    <button className="ql-align" value="right" title="Aligner à droite">
      <span style={{fontWeight:'bold'}}>⯈</span>
    </button>
    <button className="ql-align" value="justify" title="Justifier">
      <span style={{fontWeight:'bold'}}>⯀</span>
    </button>
    <button className="ql-list" value="ordered" />
    <button className="ql-list" value="bullet" />
    <button className="ql-link" />
    <button className="ql-clean" />
  </div>
);

const MailsTypes: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(DEFAULT_MAIL_TYPES[0].value);
  const [subject, setSubject] = useState(""); // Ajout de l'état pour l'objet
  const [editorValue, setEditorValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mailTypes, setMailTypes] = useState<any[]>([]);
  const [viewMail, setViewMail] = useState<any | null>(null);
  const [editMail, setEditMail] = useState<any | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const mailsPerPage = 10;
  const [search, setSearch] = useState("");
  const [sortByType, setSortByType] = useState<'asc' | 'desc' | null>(null);
  const [selectedFilterType, setSelectedFilterType] = useState<string>("all"); // "all" pour tous les types

  // États pour la gestion des types
  const [mailTypesList, setMailTypesList] = useState<MailType[]>(DEFAULT_MAIL_TYPES);
  const [typesFromFirestore, setTypesFromFirestore] = useState<MailType[]>([]); // Types avec IDs depuis Firestore
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [manageTypesModalOpen, setManageTypesModalOpen] = useState(false);
  const [newTypeValue, setNewTypeValue] = useState("");
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const [typesLoading, setTypesLoading] = useState(false);
  const [editType, setEditType] = useState<MailType | null>(null);
  const [deleteTypeId, setDeleteTypeId] = useState<string | null>(null);

  // Filtrage par recherche et par type sélectionné
  const filteredMails = mailTypes.filter(mt => {
    // Filtre par type sélectionné
    if (selectedFilterType !== "all" && mt.type !== selectedFilterType) {
      return false;
    }
    
    // Filtre par recherche
    const typeLabel = mailTypesList.find((t: MailType) => t.value === mt.type)?.label || mt.type;
    const contentText = (mt.content || '').replace(/<[^>]+>/g, '').toLowerCase();
    return (
      typeLabel.toLowerCase().includes(search.toLowerCase()) ||
      contentText.includes(search.toLowerCase())
    );
  });

  // Tri et regroupement par type
  const sortedAndGroupedMails = useMemo(() => {
    // Regrouper par type
    const grouped: { [key: string]: any[] } = {};
    filteredMails.forEach(mail => {
      const type = mail.type || 'autres';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(mail);
    });
    
    // Obtenir tous les types uniques avec leurs labels
    const allTypeKeys = Object.keys(grouped);
    const typeLabels: { [key: string]: string } = {};
    allTypeKeys.forEach(typeKey => {
      const typeInfo = mailTypesList.find((t: MailType) => t.value === typeKey);
      typeLabels[typeKey] = typeInfo ? typeInfo.label : typeKey;
    });
    
    // Trier les types par leur label
    const sortedTypeKeys = [...allTypeKeys].sort((a, b) => {
      const labelA = typeLabels[a];
      const labelB = typeLabels[b];
      if (sortByType === 'asc') {
        return labelA.localeCompare(labelB);
      } else if (sortByType === 'desc') {
        return labelB.localeCompare(labelA);
      } else {
        // Sans tri explicite, garder l'ordre des types dans mailTypesList
        const indexA = mailTypesList.findIndex(t => t.value === a);
        const indexB = mailTypesList.findIndex(t => t.value === b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return labelA.localeCompare(labelB);
      }
    });
    
    // Construire le résultat final en regroupant par type
    const result: any[] = [];
    sortedTypeKeys.forEach(type => {
      // Trier les mails de chaque type par date (plus récent en premier)
      const mailsOfType = grouped[type].sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return dateB - dateA;
      });
      result.push(...mailsOfType);
    });
    
    return result;
  }, [filteredMails, sortByType, mailTypesList]);

  const totalPages = Math.ceil(sortedAndGroupedMails.length / mailsPerPage);
  const paginatedMails = sortedAndGroupedMails.slice((currentPage - 1) * mailsPerPage, currentPage * mailsPerPage);

  // Charger les types de mails
  useEffect(() => {
    const q = query(collection(db, "MailTypes"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMailTypes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  // Charger les types de demandes depuis Firestore
  useEffect(() => {
    const q = query(collection(db, "Types"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const typesWithIds = snapshot.docs.map(doc => ({
        id: doc.id,
        value: doc.data().value,
        label: doc.data().label
      }));
      
      setTypesFromFirestore(typesWithIds);
      
      // Combiner les types par défaut avec ceux de Firestore
      const allTypes = [...DEFAULT_MAIL_TYPES];
      
      // Ajouter les types de Firestore en évitant les doublons
      typesWithIds.forEach(type => {
        if (!allTypes.find(t => t.value === type.value)) {
          allTypes.push({ value: type.value, label: type.label });
        }
      });
      
      setMailTypesList(allTypes);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (editMail) {
      setSelectedType(editMail.type);
      setSubject(editMail.subject || ""); // Pré-remplir l'objet si existant
      setEditorValue(editMail.content);
      setModalOpen(true);
    }
  }, [editMail]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (editMail) {
        await updateDoc(doc(db, "MailTypes", editMail.id), {
          type: selectedType,
          subject: subject, // Ajout de l'objet
          content: editorValue,
        });
        
        // Log de la modification
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Modification mail type",
            `Mail type modifié: ${selectedType} - ID: ${editMail.id}`
          );
        }
        
        toast.success("Mail type modifié avec succès !");
      } else {
        await addDoc(collection(db, "MailTypes"), {
          type: selectedType,
          subject: subject, // Ajout de l'objet
          content: editorValue,
          createdAt: Timestamp.now(),
        });
        
        // Log de l'ajout
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          await logUserAction(
            currentUser.email,
            "Ajout mail type",
            `Nouveau mail type ajouté: ${selectedType}`
          );
        }
        
        toast.success("Mail type ajouté avec succès !");
      }
      setModalOpen(false);
      setEditMail(null);
      setSubject(""); // Reset objet
    } catch (e: any) {
      setError("Erreur lors de l'enregistrement : " + (e.message || e));
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ajouter un nouveau type
  const handleAddType = async () => {
    if (!newTypeValue.trim() || !newTypeLabel.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    // Vérifier que la valeur n'existe pas déjà
    if (mailTypesList.find(t => t.value === newTypeValue.trim())) {
      toast.error("Un type avec cette valeur existe déjà");
      return;
    }

    setTypesLoading(true);
    try {
      // Ajouter le nouveau type à la collection Types
      await addDoc(collection(db, "Types"), {
        value: newTypeValue.trim(),
        label: newTypeLabel.trim(),
        createdAt: Timestamp.now(),
      });

      // Log de l'ajout
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Ajout type de demande",
          `Nouveau type ajouté: ${newTypeLabel.trim()}`
        );
      }

      toast.success("Type de demande ajouté avec succès !");
      setTypesModalOpen(false);
      setNewTypeValue("");
      setNewTypeLabel("");
      
      // Les types se rechargent automatiquement grâce à onSnapshot
      
    } catch (error) {
      console.error("Erreur lors de l'ajout du type:", error);
      toast.error("Erreur lors de l'ajout du type");
    } finally {
      setTypesLoading(false);
    }
  };

  // Fonction pour modifier un type
  const handleUpdateType = async () => {
    if (!editType || !editType.id) return;
    
    if (!editType.value.trim() || !editType.label.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    // Vérifier que la valeur n'existe pas déjà (sauf pour le type en cours d'édition)
    const existingType = mailTypesList.find(t => t.value === editType.value.trim() && t.value !== editType.value);
    const originalType = typesFromFirestore.find(t => t.id === editType.id);
    if (existingType && (!originalType || existingType.value !== originalType.value)) {
      toast.error("Un type avec cette valeur existe déjà");
      return;
    }

    setTypesLoading(true);
    try {
      await updateDoc(doc(db, "Types", editType.id), {
        value: editType.value.trim(),
        label: editType.label.trim(),
      });

      // Log de la modification
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Modification type de demande",
          `Type modifié: ${editType.label.trim()}`
        );
      }

      toast.success("Type modifié avec succès !");
      setEditType(null);
      
    } catch (error) {
      console.error("Erreur lors de la modification du type:", error);
      toast.error("Erreur lors de la modification du type");
    } finally {
      setTypesLoading(false);
    }
  };

  // Fonction pour supprimer un type
  const handleDeleteType = async () => {
    if (!deleteTypeId) return;

    // Vérifier si le type est utilisé dans des mails
    const typeToDelete = typesFromFirestore.find(t => t.id === deleteTypeId);
    if (!typeToDelete) {
      toast.error("Type introuvable");
      setDeleteTypeId(null);
      return;
    }

    const isUsed = mailTypes.some(mt => mt.type === typeToDelete.value);
    if (isUsed) {
      toast.error("Ce type est utilisé dans des mails types. Veuillez d'abord supprimer ou modifier les mails qui l'utilisent.");
      setDeleteTypeId(null);
      return;
    }

    setTypesLoading(true);
    try {
      await deleteDoc(doc(db, "Types", deleteTypeId));

      // Log de la suppression
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Suppression type de demande",
          `Type supprimé: ${typeToDelete.label}`
        );
      }

      toast.success("Type supprimé avec succès !");
      setDeleteTypeId(null);
      
    } catch (error) {
      console.error("Erreur lors de la suppression du type:", error);
      toast.error("Erreur lors de la suppression du type");
    } finally {
      setTypesLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    setError(null);
    try {
      await deleteDoc(doc(db, "MailTypes", deleteId));
      
      // Log de la suppression
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Suppression mail type",
          `Mail type supprimé - ID: ${deleteId}`
        );
      }
      
      setDeleteId(null);
      toast.success("Mail type supprimé !");
    } catch (e: any) {
      setError("Erreur lors de la suppression : " + (e.message || e));
      toast.error("Erreur lors de la suppression");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="societes-container" style={{ flex: 1 }}>
        <h2 className="societes-title">Mails Types</h2>
        <div style={{ marginBottom: 16, color: '#2c3e50', fontSize: '1.1rem', fontWeight: 400 }}>
          Page réservée à l'administration pour gérer les modèles de mails types.
        </div>
        <div className="actions-bar" style={{ gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="add-societe-button"
            onClick={() => {
              setSelectedType(DEFAULT_MAIL_TYPES[0].value);
              setSubject(""); // Reset objet
              setEditorValue("");
              setModalOpen(true);
            }}
          >
            + Ajouter un Mail Type
          </button>
          <button
            className="add-societe-button"
            onClick={() => setTypesModalOpen(true)}
            style={{ background: '#28a745', color: '#fff' }}
          >
            + Ajouter un Type
          </button>
          <button
            className="add-societe-button"
            onClick={() => setManageTypesModalOpen(true)}
            style={{ background: '#686de0', color: '#fff' }}
          >
            ⚙️ Gérer les Types
          </button>
          <select
            value={selectedFilterType}
            onChange={e => { setSelectedFilterType(e.target.value); setCurrentPage(1); }}
            style={{ height: 38, padding: '0 12px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box', display: 'inline-block', verticalAlign: 'middle', background: '#fff', cursor: 'pointer' }}
          >
            <option value="all">Tous les types</option>
            {mailTypesList.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Rechercher par type ou contenu..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            style={{ height: 38, width: 400, padding: '0 12px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box', display: 'inline-block', verticalAlign: 'middle' }}
          />
        </div>

        {/* MODALE */}
        {modalOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, minWidth: 600, maxWidth: 900, width: '100%', maxHeight: '90vh', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0 }}>Ajouter un Mail Type</h3>
                          <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label htmlFor="mail-type-select" style={{ fontWeight: 500 }}>Type de demande :</label>
                <button
                  onClick={() => setTypesModalOpen(true)}
                  style={{
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1
                  }}
                  title="Ajouter un nouveau type de demande"
                >
                  +
                </button>
              </div>
              <select
                id="mail-type-select"
                value={selectedType}
                onChange={e => setSelectedType(e.target.value)}
                style={{ marginTop: 8, padding: 6, borderRadius: 4, width: '100%' }}
              >
                {mailTypesList.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
              {/* Champ Objet */}
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="mail-subject-input" style={{ fontWeight: 500 }}>Objet :</label><br />
                <input
                  id="mail-subject-input"
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Objet du mail"
                  style={{ marginTop: 8, padding: 6, borderRadius: 4, width: '100%', border: '1px solid #ccc' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 500 }}>Contenu du mail :</label>
                <CustomToolbar />
                <ReactQuill
                  theme="snow"
                  value={editorValue}
                  onChange={setEditorValue}
                  style={{ height: 350, marginTop: 8 }}
                  modules={{
                    toolbar: {
                      container: "#custom-toolbar"
                    },
                    clipboard: { matchVisual: false },
                  }}
                />
              </div>
              <div className="mailtype-modal-actions">
                <button className="cancel-button" onClick={() => setModalOpen(false)} disabled={loading}>
                  Annuler
                </button>
                <button className="submit-button" style={{ background: '#686de0', color: '#fff' }} onClick={handleSave} disabled={loading}>
                  {loading ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
              {error && <div style={{ color: 'red', marginTop: 12 }}>{error}</div>}
              {success && <div style={{ color: 'green', marginTop: 12 }}>{success}</div>}
            </div>
          </div>
        )}

        {/* Tableau des mails enregistrés */}
        {mailTypes.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', background: '#fff', borderCollapse: 'collapse', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', minWidth: 600 }}>
                <thead>
                  <tr style={{ background: '#f2f2f2' }}>
                    <th style={{ padding: 8, textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        Type
                        <button
                          onClick={() => {
                            if (sortByType === null) setSortByType('asc');
                            else if (sortByType === 'asc') setSortByType('desc');
                            else setSortByType(null);
                            setCurrentPage(1);
                          }}
                          style={{
                            background: sortByType ? '#686de0' : 'transparent',
                            color: sortByType ? '#fff' : '#333',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: '2px 8px',
                            cursor: 'pointer',
                            fontSize: 12
                          }}
                          title={sortByType === 'asc' ? 'Tri croissant' : sortByType === 'desc' ? 'Tri décroissant' : 'Trier par type'}
                        >
                          {sortByType === 'asc' ? '↑' : sortByType === 'desc' ? '↓' : '⇅'}
                        </button>
                      </div>
                    </th>
                    <th style={{ padding: 8, textAlign: 'left' }}>Objet</th>
                    <th style={{ padding: 8, textAlign: 'left' }}>Contenu (extrait)</th>
                    <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
                    <th style={{ padding: 8, textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMails.map((mt, index) => {
                    const currentType = mt.type || 'autres';
                    const previousType = index > 0 ? (paginatedMails[index - 1].type || 'autres') : null;
                    const typeLabel = mailTypesList.find(t => t.value === currentType)?.label || currentType;
                    const isNewTypeGroup = currentType !== previousType;
                    
                    return (
                      <React.Fragment key={mt.id}>
                        {isNewTypeGroup && index > 0 && (
                          <tr>
                            <td colSpan={5} style={{ padding: '8px 0', borderTop: '2px solid #e0e0e0' }}></td>
                          </tr>
                        )}
                        <tr style={{ background: isNewTypeGroup ? '#f8f9fa' : 'transparent' }}>
                          <td style={{ padding: 8, fontWeight: isNewTypeGroup ? 600 : 400 }}>
                            {isNewTypeGroup ? (
                              <span style={{ color: '#686de0' }}>{typeLabel}</span>
                            ) : (
                              typeLabel
                            )}
                          </td>
                          <td style={{ padding: 8, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mt.subject || ''}</td>
                          <td style={{ padding: 8, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(mt.content || '').replace(/<[^>]+>/g, '').slice(0, 80)}{(mt.content || '').length > 80 ? '…' : ''}</td>
                          <td style={{ padding: 8 }}>{mt.createdAt && mt.createdAt.toDate ? mt.createdAt.toDate().toLocaleString('fr-FR') : ''}</td>
                          <td style={{ padding: 8, display: 'flex', gap: 8 }}>
                            <button onClick={() => setViewMail(mt)} style={{ background: '#dfe6e9', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Voir</button>
                            <button onClick={() => setEditMail(mt)} style={{ background: '#686de0', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Éditer</button>
                            <button onClick={() => setDeleteId(mt.id)} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Supprimer</button>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button className="cancel-button" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Précédent</button>
              <span style={{ fontSize: 14 }}>Page {currentPage} / {totalPages}</span>
              <button className="submit-button" style={{ background: '#686de0', color: '#fff' }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Suivant</button>
            </div>
          </div>
        )}

        {/* Modale Voir */}
        {viewMail && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, minWidth: 600, maxWidth: 900, width: '100%', maxHeight: '90vh', boxShadow: '0 4px 32px rgba(0,0,0,0.18)', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0, marginBottom: 20 }}>Mail Type : {mailTypesList.find(t => t.value === viewMail.type)?.label || viewMail.type}</h3>
              <div style={{ marginBottom: 18 }}><b>Objet :</b> {viewMail.subject || <span style={{ color: '#aaa' }}>(Aucun objet)</span>}</div>
              <div
                className="mailtype-viewer ql-editor"
                dangerouslySetInnerHTML={{ __html: viewMail.content }}
                style={{
                  border: '1px solid #eee',
                  borderRadius: 6,
                  padding: 16,
                  background: '#fafbfc',
                  minHeight: 120,
                  marginBottom: 32,
                  fontSize: 16,
                  color: '#555',
                }}
              />
              <div style={{ textAlign: 'right', marginTop: 24 }}>
                <button className="cancel-button" onClick={() => setViewMail(null)}>Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* Modale confirmation suppression */}
        {deleteId && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, minWidth: 400, maxWidth: 500, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
              <h3>Confirmer la suppression</h3>
              <p>Voulez-vous vraiment supprimer ce mail type ?</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 32 }}>
                <button className="cancel-button" onClick={() => setDeleteId(null)} disabled={loading}>Annuler</button>
                <button className="submit-button" style={{ background: '#e74c3c', color: '#fff' }} onClick={handleDelete} disabled={loading}>{loading ? 'Suppression...' : 'Supprimer'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Modale pour ajouter un nouveau type */}
        {typesModalOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001
          }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, minWidth: 400, maxWidth: 500, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
              <h3 style={{ marginTop: 0 }}>Ajouter un nouveau type de demande</h3>
              
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="new-type-value" style={{ fontWeight: 500 }}>Valeur (code) :</label><br />
                <input
                  id="new-type-value"
                  type="text"
                  value={newTypeValue}
                  onChange={e => setNewTypeValue(e.target.value)}
                  placeholder="Ex: formation, audit, etc."
                  style={{ marginTop: 8, padding: 8, borderRadius: 4, width: '100%', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label htmlFor="new-type-label" style={{ fontWeight: 500 }}>Libellé :</label><br />
                <input
                  id="new-type-label"
                  type="text"
                  value={newTypeLabel}
                  onChange={e => setNewTypeLabel(e.target.value)}
                  placeholder="Ex: Formation, Audit, etc."
                  style={{ marginTop: 8, padding: 8, borderRadius: 4, width: '100%', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button 
                  className="cancel-button" 
                  onClick={() => {
                    setTypesModalOpen(false);
                    setNewTypeValue("");
                    setNewTypeLabel("");
                  }} 
                  disabled={typesLoading}
                >
                  Annuler
                </button>
                <button 
                  className="submit-button" 
                  style={{ background: '#28a745', color: '#fff' }} 
                  onClick={handleAddType} 
                  disabled={typesLoading}
                >
                  {typesLoading ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale pour modifier un type */}
        {editType && editType.id && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001
          }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, minWidth: 400, maxWidth: 500, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
              <h3 style={{ marginTop: 0 }}>Modifier le type de demande</h3>
              
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="edit-type-value" style={{ fontWeight: 500 }}>Valeur (code) :</label><br />
                <input
                  id="edit-type-value"
                  type="text"
                  value={editType.value}
                  onChange={e => setEditType({ ...editType, value: e.target.value })}
                  placeholder="Ex: formation, audit, etc."
                  style={{ marginTop: 8, padding: 8, borderRadius: 4, width: '100%', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label htmlFor="edit-type-label" style={{ fontWeight: 500 }}>Libellé :</label><br />
                <input
                  id="edit-type-label"
                  type="text"
                  value={editType.label}
                  onChange={e => setEditType({ ...editType, label: e.target.value })}
                  placeholder="Ex: Formation, Audit, etc."
                  style={{ marginTop: 8, padding: 8, borderRadius: 4, width: '100%', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button 
                  className="cancel-button" 
                  onClick={() => setEditType(null)} 
                  disabled={typesLoading}
                >
                  Annuler
                </button>
                <button 
                  className="submit-button" 
                  style={{ background: '#686de0', color: '#fff' }} 
                  onClick={handleUpdateType} 
                  disabled={typesLoading}
                >
                  {typesLoading ? 'Modification...' : 'Modifier'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale de confirmation pour supprimer un type */}
        {deleteTypeId && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001
          }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 40, minWidth: 400, maxWidth: 500, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.18)' }}>
              <h3 style={{ marginTop: 0 }}>Confirmer la suppression</h3>
              <p>
                Voulez-vous vraiment supprimer le type "{typesFromFirestore.find(t => t.id === deleteTypeId)?.label || 'inconnu'}" ?
              </p>
              <p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                Note: Vous ne pouvez pas supprimer un type qui est utilisé dans des mails types.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                <button 
                  className="cancel-button" 
                  onClick={() => setDeleteTypeId(null)} 
                  disabled={typesLoading}
                >
                  Annuler
                </button>
                <button 
                  className="submit-button" 
                  style={{ background: '#e74c3c', color: '#fff' }} 
                  onClick={handleDeleteType} 
                  disabled={typesLoading}
                >
                  {typesLoading ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale de gestion des types */}
        {manageTypesModalOpen && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001
          }}>
            <div style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: 40, 
              minWidth: 600, 
              maxWidth: 800, 
              width: '100%', 
              maxHeight: '90vh',
              boxShadow: '0 4px 32px rgba(0,0,0,0.18)',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h3 style={{ marginTop: 0, marginBottom: 0 }}>Gérer les types</h3>
                <button
                  onClick={() => setManageTypesModalOpen(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#666',
                    padding: 0,
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Fermer"
                >
                  ×
                </button>
              </div>

              {typesFromFirestore.length > 0 ? (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                    gap: 12 
                  }}>
                    {typesFromFirestore.map(type => (
                      <div 
                        key={type.id} 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: 16,
                          background: '#f8f9fa',
                          borderRadius: 8,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: 4 }}>{type.label}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>Code: {type.value}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                          <button
                            onClick={() => {
                              setEditType({ ...type });
                              setManageTypesModalOpen(false);
                            }}
                            style={{
                              background: '#686de0',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '6px 12px',
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                            title="Modifier"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => {
                              setDeleteTypeId(type.id!);
                              setManageTypesModalOpen(false);
                            }}
                            style={{
                              background: '#e74c3c',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '6px 12px',
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                            title="Supprimer"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ 
                  padding: 24, 
                  background: '#f8f9fa', 
                  borderRadius: 8, 
                  color: '#666',
                  textAlign: 'center'
                }}>
                  Aucun type personnalisé. Les types par défaut ne peuvent pas être modifiés.
                </div>
              )}

              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 12,
                paddingTop: 16,
                borderTop: '1px solid #e0e0e0'
              }}>
                <button 
                  className="cancel-button" 
                  onClick={() => setManageTypesModalOpen(false)}
                >
                  Fermer
                </button>
                <button 
                  className="submit-button" 
                  style={{ background: '#28a745', color: '#fff' }} 
                  onClick={() => {
                    setManageTypesModalOpen(false);
                    setTypesModalOpen(true);
                  }}
                >
                  + Ajouter un nouveau type
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
      <Footer />
    </div>
  );
};

export default MailsTypes; 